<template>
  <Upload
    name="file"
    listType="picture-card"
    className="avatar-uploader"
    :headers="state.upLoadHeaders"
    :action="state.upLoadUrl"
    :beforeUpload="beforeUpload"
    :data="data"
    v-model:file-list="fileLists"
    @change="handleChange"
    @remove="handleRemove"
    :accept="accept"
    @preview="handlePreview"
  >
    <div v-if="fileList?.length < limit">
      <LoadingOutlined v-if="state.loading"></LoadingOutlined>
      <div v-else>
        <PlusOutlined></PlusOutlined>
        <div class="ant-upload-text">{{ $lang('上传图片') }}</div>
      </div>
    </div>
  </Upload>
   <Image
      :width="200"
      :style="{ display: 'none' }"
      :preview="{
        visible: modalState.visible,
        onVisibleChange: (visible) => {
          modalState.visible = visible
        },
      }"
      :src="previewImage"
    />
</template>

<script lang="ts" setup>
  import { reactive, ref, watch } from 'vue'
  import { Upload, message, Modal, Image } from 'ant-design-vue'
  import { PlusOutlined, LoadingOutlined } from '@ant-design/icons-vue'
  import { isSuccess } from '@/utils/index'
  import { useAuthStore } from '@/store/auth'
  import { lang } from '@/utils/index'
  import { useModal } from '@xinghai/site-common'
  const authStore = useAuthStore()
  const VITE_APP_UPLOAD_URL = import.meta.env.VITE_APP_UPLOAD_URL
  interface FileItem {
    uid?: string
    name?: string
    status?: string
    response?: string
    percent?: number
    url?: string
    preview?: string
    originFileObj?: any
  }
  interface Props {
    fileList?: FileItem[]
    imageSize?: number
    limit?: number
    value: string[]
    accept?: string
    data?: Record<string, any>
    fileListPaths?: string[]
  }
  const props = withDefaults(defineProps<Props>(), {
    limit: 1,
    accept: '.png,.jpg,.jpeg',
    data: { fileLifeEnum: 'FOREVER', returnUrl: true }
  })
  interface StateIn {
    loading: boolean
    upLoadHeaders: { [key: string]: any }
    upLoadUrl: string
  }
  const fileLists = ref<FileItem[]>([])
  watch(
    () => props.fileList,
    (val) => {
      fileLists.value = val
    }
  )
  interface FileInfo {
    file: FileItem
    fileList: FileItem[]
  }
  const previewVisible = ref<boolean>(false)
  const previewImage = ref<string | undefined>('')
  const { modalState, openModal, closeModal } = useModal({
    visible: previewVisible.value,
    footer: null
  })
  const getBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }
  const state: StateIn = reactive({
    loading: false,
    upLoadHeaders: {
      Authtoken: authStore.token,
      sysCode: 'ADMIN'
    },
    upLoadUrl: VITE_APP_UPLOAD_URL
  })
  const handlePreview = async (file: FileItem) => {
    if (!file.url && !file.preview) {
      file.preview = (await getBase64(file.originFileObj)) as unknown as string
    }
    previewImage.value = file.url || file.preview
    openModal()
  }
  const handleRemove = (file: Record<string, any>) => {
    const index = props.fileList.findIndex((_) => _.uid === file.uid)
    props.fileListPaths.splice(index, 1)
    return true
  }
  const beforeUpload = (file: Blob) => {
    const isJpgOrPng = props.accept.split(',').includes('.' + file.type.split('/')[1])
    if (!isJpgOrPng) {
      message.error(lang('不支持此格式文件!'))
      return Upload.LIST_IGNORE
    }
    const imageSize = props.imageSize || 5
    const isLt2MB = (file.size / 1024) < 1024 * imageSize
    if (!isLt2MB) {
      message.error(lang(`图片大小必须小于${imageSize}MB!`))
      return Upload.LIST_IGNORE
    }
    return isJpgOrPng && isLt2MB
  }
  const emit = defineEmits(['update:value', 'savePath'])

  const handleChange = ({ fileList: newFileList }: FileInfo) => {
    if (newFileList.map((_) => _.status).includes('uploading')) {
      state.loading = true
      return
    }
    state.loading = false
    const ress = newFileList[newFileList.length - 1]?.response
    if (!ress) {
      emit(
        'update:value',
        newFileList.map((_) => _.url)
      )
    } else if (isSuccess(ress)) {
      emit('update:value', [...props.value, ress?.data?.url])
      emit('savePath', [...props.fileListPaths, ress?.data?.path])
    } else {
      message.error(lang('上传出错'))
    }
  }
</script>
